'use client'

import { useTranslations } from 'next-intl'
import { useMemo } from 'react'
import { IRestaurantReward } from '@/api/restaurants/types/restaurant-types'
import { Button } from '@/components/button'
import { useRestaurantDetailContext } from '@/features/restaurants/restaurant-detail/restaurant-detail-context'
import { noOp } from '@/utils/functions/noOp'
import { COLORS } from '@/utils/styles/colors'

interface IRestaurantRewardCardProps {
    reward: IRestaurantReward
}

export const RestaurantRewardCard = ({ reward }: IRestaurantRewardCardProps) => {
    const { restaurant, restaurantSlug, citySlug } = useRestaurantDetailContext()

    const t = useTranslations()

    const link = useMemo(() => {
        if (!restaurant) return

        return `https://app.adjust.com/147kxpz8?campaign=RestaurantDetail&adgroup=${restaurantSlug}-${citySlug}&engagement_type=fallback_click`
    }, [restaurant, restaurantSlug, citySlug])

    return (
        <div
            className="rounded-l bg-bg-neutral-surface py-4 w-full shadow-soft justify-between
                min-h-full flex flex-col"
        >
            <div className="w-full overflow-hidden">
                <h3 className="text-h4 md:text-h3 mb-3.5 px-5">{reward.name}</h3>

                <div className="relative">
                    <div className="flex flex-row gap-1 mb-3.5 px-5 overflow-y-hidden overflow-x-scroll scrollbar-hide relative">
                        <RestaurantRewardCardTag
                            label={t('restaurants.detail.rewards.checkins', {
                                days: reward.requiredCheckIns ?? '',
                            })}
                        />
                    </div>
                </div>

                <p className="text-fg-secondary mb-3.5 text-p-sm md:text-p-md px-5">
                    {reward.description}
                </p>
            </div>

            <div className="px-5 w-full">
                <Button
                    {...(link ? { href: { externalUrl: link } } : { onClick: noOp })}
                    styleType="tertiary"
                    backgroundColor={COLORS.bg.inverseBrand}
                    contentColor={COLORS.fg.inverse}
                    borderColor="transparent"
                    width="100%"
                >
                    {t('restaurants.detail.rewards.cta')}
                </Button>
            </div>
        </div>
    )
}

interface IRestaurantRewardCardTagProps {
    icon?: React.ReactNode
    label: string
}

const RestaurantRewardCardTag = ({ icon, label }: IRestaurantRewardCardTagProps) => {
    return (
        <div
            className="rounded-full min-h-[32px] max-h-[32px] flex items-center justify-center w-fit gap-x-1 px-3 bg-bg-accent-01"
            style={{
                border: `2px solid ${COLORS.border.primary}`,
            }}
        >
            {icon && <span>{icon}</span>}

            <p className="text-label-sm whitespace-nowrap">{label}</p>
        </div>
    )
}
